import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { parseInt } from 'common/helpers';
import {
  IChangeGroupStyles,
  IElement,
  IStickyPoints,
  IStylesByElemId,
} from 'common/interfaces';
import { ResizeHandlerPositions } from 'hooks/hooks.interfaces';
import { changeGroupElemStyles } from 'store/reducers/elems.slice';

import { ResizeService } from './ResizeService';

export class GroupResizeService extends ResizeService {
  public initialElemWidth: number;
  public initialElemHeight: number;

  constructor(
    elem: HTMLElement,
    setElemSize: (
      value: React.SetStateAction<{
        width: number;
        height: number;
      }>,
    ) => void,
    pressedKeys: string[],
    readonly stickyPoints: IStickyPoints[],
    public groupElem: IElement,
    public readonly dispatch: Dispatch<AnyAction>,
  ) {
    super(elem, setElemSize, pressedKeys, stickyPoints);

    this.initialElemWidth = parseInt(elem.style.width);
    this.initialElemHeight = parseInt(elem.style.height);

    this.dispatch = dispatch;
  }

  _transformChildren(
    children: IElement[],
    deltaX: number,
    deltaY: number,
  ): IStylesByElemId[] {
    const transformed: IStylesByElemId[] = [];

    for (const item of children) {
      transformed.push({
        id: item.id,
        styles: {
          left: parseInt(item.styles.left) * (deltaX + 1) + 'px',
          top: parseInt(item.styles.top) * (deltaY + 1) + 'px',
          width: parseInt(item.styles.width) * (1 + deltaX) + 'px',
          height: parseInt(item.styles.height) * (1 + deltaY) + 'px',
        },
      });
    }

    return transformed;
  }

  _changeNestedElemsStyles(returnValue: boolean): IChangeGroupStyles | null {
    const newStyles = this.domElem.style;

    const diffX = parseInt(newStyles.width) - this.initialElemWidth;
    const diffY = parseInt(newStyles.height) - this.initialElemHeight;

    const deltaX = diffX / this.initialElemWidth;
    const deltaY = diffY / this.initialElemHeight;

    const newNestedElemsCollection: IStylesByElemId[] | null = this.groupElem
      .children
      ? this._transformChildren(this.groupElem.children, deltaX, deltaY)
      : null;

    const changedGroupData: IChangeGroupStyles = {
      id: this.groupElem.id,
      styles: {
        top: this.domElem.style.top,
        left: this.domElem.style.left,
        width: this.domElem.style.width,
        height: this.domElem.style.height,
      },
      children: newNestedElemsCollection,
    };

    if (returnValue) {
      return changedGroupData;
    } else {
      this.dispatch(changeGroupElemStyles(changedGroupData));
      return null;
    }
  }

  sideHorizontalResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
    returnValue = false,
  ): IChangeGroupStyles | null {
    super.sideHorizontalResize(handlerType, pageX, pageY);

    return this._changeNestedElemsStyles(returnValue);
  }

  sideVerticalResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
    returnValue = false,
  ): IChangeGroupStyles | null {
    super.sideVerticalResize(handlerType, pageX, pageY);

    return this._changeNestedElemsStyles(returnValue);
  }

  cornerResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
    pressedKeys: string[],
    returnValue = false,
  ): IChangeGroupStyles | null {
    super.cornerResize(handlerType, pageX, pageY, pressedKeys);

    return this._changeNestedElemsStyles(returnValue);
  }
}
